import React, { ReactNode, forwardRef, useEffect, useState } from 'react';
import Upload from 'rc-upload';
import { Line } from 'rc-progress';
import { multiply, number, inv, format } from 'mathjs';
import { Button, Modal } from '@/shared/components';
import classnames from 'classnames';

export interface FileUploaderProps {
  uploadTxt: ReactNode;
  outlineBtnTxt: string;
  primaryBtnTxt: string;
  downloadTxt?: string;
  supportedFileTypes: string;
  successTxt: string;
  uploadFunc?: (file: any) => void;
  progressFinishedUploadFunc?: (file: any) => void;
  resetRefresh?: () => void;
  refresh?: boolean;
  downloadFile?: string;
  modalTitle?: string;
  modalContent?: ReactNode;
  modalSaveBtnLabel?: string;
  showSize?: boolean;
  className?: string;
  customRemove?: ReactNode;
  needIcon?: boolean;
  fileRemoved?: (isRemoved: boolean) => void;
  hideDisabledRemove?: boolean;
  maxFileSize?: number;
  fileSizeErrorTxt?: string;
}
const FileUploader = forwardRef<HTMLInputElement, FileUploaderProps>(function FileUploader(props, ref) {
  const {
    uploadTxt,
    outlineBtnTxt,
    primaryBtnTxt,
    downloadTxt,
    supportedFileTypes,
    successTxt,
    uploadFunc,
    refresh,
    resetRefresh,
    downloadFile,
    modalTitle,
    modalContent,
    modalSaveBtnLabel,
    showSize = true,
    className,
    customRemove,
    progressFinishedUploadFunc,
    needIcon = true,
    fileRemoved,
    hideDisabledRemove = false,
    maxFileSize,
    fileSizeErrorTxt,
  } = props;
  const [percentVal, setPercentVal] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [fileName, setFileName] = useState<string>('');
  const [fileSize, setFileSize] = useState<number | string>(0);
  const [success, setSuccess] = useState(false);
  const [fileData, setFileData] = useState('');
  const [showPopUp, setShowPopUp] = useState(false);
  const [fileSizeError, setFileSizeError] = useState(false);
  const title = <span className="sm:text-xl">{modalTitle}</span>;
  useEffect(() => {
    if (refresh) {
      resetUploader();
    }
  }, [refresh]);

  const uploadProps = {
    multiple: false,
    disabled: success,
    accept: supportedFileTypes,
    customRequest: (options) => {
      const { file, onProgress } = options;
      onProgress({ percent: 100 }, file);
    },
    onProgress({ percent }, file) {
      setPercentVal(percent);
      if (percent === 100) {
        setTimeout(() => {
          setSuccess(true);
          setIsUploading(false);
          setFileData(file);
          progressFinishedUploadFunc && progressFinishedUploadFunc(file);
        }, 1e3);
      }
    },
    beforeUpload(file, fileList) {
      setIsUploading(true);
      setFileName(file.name);
      const size = format(multiply(inv(1024), number(file.size)), { notation: 'fixed', precision: 2 });
      setFileSize(size);
      if (maxFileSize && file.size > maxFileSize) {
        setIsUploading(false);
        setFileSizeError(true);
        return false;
      } else {
        setFileSizeError(false);
      }
      return true;
    },
  };

  const removeFile = () => {
    setFileName('');
    setFileSize(0);
    setIsUploading(false);
    setSuccess(false);
    setPercentVal(0);
    setShowPopUp(false);
    fileRemoved && fileRemoved(true);
  };
  const resetUploader = () => {
    removeFile();
    if (resetRefresh) {
      resetRefresh();
    }
  };

  return (
    <div>
      <Upload {...uploadProps} className="cursor-pointer">
        <div className={classnames('c-dropzone w-full h-auto flex items-center justify-center', className)}>
          <div className="flex items-center justify-center py-5">
            <i className="icon-32-cloudUpload-mono" />
            {!success && <p dangerouslySetInnerHTML={{ __html: uploadTxt }}></p>}
            {success && <p dangerouslySetInnerHTML={{ __html: successTxt }}></p>}
          </div>
        </div>
      </Upload>
      <div className="pt-5 px-2 flex">
        <div>
          {!success && downloadTxt && (
            <a href={downloadFile} download>
              {downloadTxt}
            </a>
          )}
          {isUploading && (
            <div className="min-w-[300px]">
              <Line percent={percentVal} strokeWidth={2} trailWidth={2} trailColor="#FFF" strokeColor={'#e71316'} />
            </div>
          )}
          {success && (
            <div>
              <div className="c-uploader__files-list">
                <div className="c-uploader-file">
                  <div className="c-uploader-file__left-info-container">
                    <div className="c-uploader-file__left-info">
                      {needIcon && (
                        <>
                          <span className="icon-24-docs-xls-mono c-uploader-file__icon-blue"></span>
                          <div className="mr-4"></div>
                        </>
                      )}
                      <span className="icon-checkmark-mono c-uploader-file__icon-blue"></span>
                      <div className="mr-2"></div>
                      <div className="c-uploader-file__name c-uploader-file__name--completed">
                        <div className="c-uploader-file__ellipsis-text" title={fileName}>
                          {fileName}
                        </div>
                      </div>
                    </div>
                  </div>
                  {showSize && <div className="c-uploader-file__right-info">{fileSize}kb</div>}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="ml-auto">
          {customRemove && success && (
            <div
              onClick={() => {
                setShowPopUp(true);
              }}
            >
              {customRemove}
            </div>
          )}
          {!customRemove && (!hideDisabledRemove || success) && (
            <Button
              type="outline"
              label={outlineBtnTxt}
              disabled={!success}
              onClick={() => {
                setShowPopUp(true);
              }}
            />
          )}

          {success && !progressFinishedUploadFunc && (
            <Button
              className="ml-4"
              label={primaryBtnTxt}
              onClick={() => {
                uploadFunc(fileData);
              }}
            />
          )}
          {fileSizeError && <div className="text-red-500">{fileSizeErrorTxt}</div>}
        </div>
        <Modal
          title={title}
          size="medium"
          isOpen={showPopUp}
          handleClose={() => setShowPopUp(false)}
          handleCancel={() => setShowPopUp(false)}
          saveButtonLabel={modalSaveBtnLabel}
          disableSaveButton={false}
          handleSave={removeFile}
        >
          {modalContent}
        </Modal>
      </div>
    </div>
  );
});
export { FileUploader };
